import PropTypes from 'prop-types';
import React, { useContext, useState, useCallback, useEffect, memo } from 'react';
import { Map } from 'immutable';

import { PrettyErrorContext } from '../../contexts/PrettyErrorContext';

const emptyMap = Map();

export default function PrettyErrorDecorator(Component) {
  const displayName = `PrettyError(${Component.displayName || Component.name})`;

  const PrettyError = memo(
    ({
      name,
      error: errorProp,
      onErrorClose: onErrorCloseProp,
      showErrorOnFocus,
      onBlur,
      onFocus,
      canDisplayError: initialCanDisplayError = false,
      ...props
    }) => {
      const { onErrorClose: contextOnErrorClose, inputErrors = emptyMap } = useContext(PrettyErrorContext);

      const error = errorProp !== undefined ? errorProp : inputErrors.get(name, '');
      const onErrorClose = onErrorCloseProp ?? contextOnErrorClose;

      const [canDisplayError, setCanDisplayError] = useState(initialCanDisplayError);

      useEffect(() => {
        if (initialCanDisplayError) setCanDisplayError(true);
      }, [initialCanDisplayError]);

      useEffect(() => {
        if (!canDisplayError && inputErrors?.get?.('errorsFromApi')) {
          setCanDisplayError(true);
        }
      }, [canDisplayError, inputErrors]);

      const handleBlur = useCallback(
        event => {
          setCanDisplayError(true);
          onBlur?.(event);
        },
        [onBlur],
      );

      const handleFocus = useCallback(
        (...args) => {
          if (!showErrorOnFocus) {
            setImmediate(() => {
              if (typeof onErrorClose === 'function') onErrorClose(args[0], 'errorsFromApi');
              setCanDisplayError(false);
            });
          }
          onFocus?.(...args);
        },
        [showErrorOnFocus, onErrorClose, onFocus],
      );

      let errorMessage = null;

      if (canDisplayError && error) {
        if (process.env.NODE_ENV !== 'production' && !onErrorClose) {
          console.error(`${displayName} has error but no onErrorClose callback provided`);
        }
        errorMessage = error === true ? 'Some error happened' : error;
      }

      return <Component name={name} {...props} error={errorMessage} onBlur={handleBlur} onFocus={handleFocus} />;
    },
  );

  PrettyError.propTypes = {
    canDisplayError: PropTypes.bool,
    error: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onErrorClose: PropTypes.func,
    onFocus: PropTypes.func,
    showErrorOnFocus: PropTypes.bool,
  };

  PrettyError.displayName = displayName;

  return PrettyError;
}
